import { ApiError, createApiClient } from '@amumin/api-client';
import { appMessagesFor, t, type Messages } from '@amumin/i18n';

import { movedAwareFetch } from './lib/moved.js';

// Eén client voor de ouderapp: same-origin, cookies gaan mee (fb_sess op Path=/). De Vite-dev-
// server proxyt /v1 naar de API of naar de mock (apps/web/mock/server.mjs). Een 401 `moved` (de
// app is verhuisd, lib/moved.ts) stuurt het tabblad naar het nieuwe adres.
// De taal van deze login gaat als Accept-Language mee (oprichter 1 okt: alles in zes talen): de API
// maakt teksten zoals de samenvatting van een herhaling dan in die taal (state/session.ts zet hem).
let requestLocale: string | null = null;

export function setRequestLocale(code: string | null): void {
  requestLocale = code;
}

function withLocale(init: RequestInit | undefined): RequestInit | undefined {
  if (!requestLocale) return init;
  const headers = new Headers(init?.headers);
  headers.set('Accept-Language', requestLocale);
  return { ...init, headers };
}

export const api = createApiClient({
  fetch: movedAwareFetch((input, init) => fetch(input, withLocale(init))),
});

export { ApiError };

export function isApiError(e: unknown, status?: number, code?: string): e is ApiError {
  if (!(e instanceof ApiError)) return false;
  if (status !== undefined && e.status !== status) return false;
  if (code !== undefined && e.code !== code) return false;
  return true;
}

/** Sessie kwijt (401 zonder reauth) of buiten een vertrouwd netwerk (401 reauth_required). */
export function isAuthLost(e: unknown): boolean {
  return isApiError(e, 401);
}

/** Vertaalt een fout naar één regel voor de gebruiker. */
export function errorMessage(e: unknown, m: Messages): string {
  const w = m.web;
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'personal_space_private':
        return appMessagesFor(m.locale).omgeving.errors.personalSpacePrivate;
      case 'calendar_provider_not_configured':
        return appMessagesFor(m.locale).omgeving.errors.providerNotConfigured;
      case 'calendar_already_connected':
        return appMessagesFor(m.locale).omgeving.errors.alreadyConnected;
      case 'invalid_credentials':
        return w.invalidCredentials;
      case 'email_not_verified':
        return w.emailNotVerified;
      case 'email_taken':
        return w.emailTaken;
      case 'password_breached':
        return w.passwordBreached;
      case 'rate_limited': {
        const details = e.details as { retry_after_s?: number } | undefined;
        return t(w.rateLimited, { seconds: details?.retry_after_s ?? 60 });
      }
      case 'locked_out':
      case 'challenge_locked':
        return w.lockedOut;
      case 'version_conflict':
        return w.versionConflict;
      case 'consent_self_required':
        return w.consentSelfRequired;
      case 'reauth_required':
        return w.outsideTrustedNetwork;
      case 'household_paused':
        return w.householdPaused;
      // 0065: proefmaand of abonnement voorbij (alleen kijken tot er betaald is).
      case 'subscription_expired':
        return appMessagesFor(m.locale).billing.banner.readOnly;
      case 'checkout_open':
        return appMessagesFor(m.locale).billing.page.checkoutOpen;
      case 'provider_unavailable':
        return appMessagesFor(m.locale).billing.page.soon;
      case 'step_up_required':
        return w.stepUpTitle;
      case 'invalid_network_value': {
        const details = e.details as { reason?: string } | undefined;
        if (details?.reason === 'ipv6_not_supported_yet') return w.valueIpv6;
        if (details?.reason === 'cgnat') return w.valueCgnat;
        return w.valueInvalid;
      }
      case 'not_found':
        return w.errorNotFound;
      case 'module_off':
        return w.moduleOff;
      default:
        if (e.status === 401) return w.sessionExpired;
        return w.errorGeneric;
    }
  }
  if (e instanceof TypeError) return w.errorNetwork;
  return w.errorGeneric;
}
